import { ResumeSectionType } from '@applicator/shared'

import type { QualityIssue } from '../../lib/optimization/qualityEvalFlagging'
import { qualityIssueSectionTypes } from '../../lib/optimization/qualityEvalFlagging'
import type { SkillGapSummary } from '../../lib/optimization/skillGapRecommendations'
import { skillGapHasActionItems } from '../../lib/optimization/skillGapRecommendations'

/** Stable key for auto-expand: only changes when the flagged section set changes. */
export function qualityFlaggedSectionsExpandKey(
  issues: QualityIssue[],
  skillGapSummary: SkillGapSummary | null | undefined
): string {
  const types = qualityIssueSectionTypes(issues)
  if (skillGapHasActionItems(skillGapSummary)) {
    types.push('skills')
  }
  if (!types.length) return ''
  return [...new Set(types)].sort().join('\0')
}

/** Sections newly flagged since the previous expand key (ignores resolved sections). */
export function qualityFlaggedSectionsNewlyAdded(
  previousKey: string,
  nextKey: string
): ResumeSectionType[] {
  if (!nextKey) return []
  const prev = new Set(previousKey ? previousKey.split('\0') : [])
  return (nextKey.split('\0') as ResumeSectionType[]).filter((section) => !prev.has(section))
}

/** Margin (px) when deciding if an element is already visible inside the left editor panel. */
export const LEFT_PANEL_SCROLL_MARGIN_PX = 12

export function isElementVisibleInScrollContainer(
  element: Element,
  container: HTMLElement,
  marginPx: number = LEFT_PANEL_SCROLL_MARGIN_PX
): boolean {
  const containerRect = container.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  return (
    elementRect.top >= containerRect.top + marginPx &&
    elementRect.bottom <= containerRect.bottom - marginPx
  )
}

/** Scroll only when the element is outside the panel viewport; minimal delta, not centered. */
export function scrollElementIntoPanelIfNeeded(
  element: HTMLElement,
  panel: HTMLElement,
  options?: { marginPx?: number; behavior?: ScrollBehavior }
): boolean {
  const marginPx = options?.marginPx ?? LEFT_PANEL_SCROLL_MARGIN_PX
  const behavior = options?.behavior ?? 'smooth'

  if (isElementVisibleInScrollContainer(element, panel, marginPx)) {
    return false
  }

  const panelRect = panel.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  let targetScroll = panel.scrollTop

  if (elementRect.top < panelRect.top + marginPx) {
    targetScroll += elementRect.top - panelRect.top - marginPx
  } else if (elementRect.bottom > panelRect.bottom - marginPx) {
    targetScroll += elementRect.bottom - panelRect.bottom + marginPx
  }

  panel.scrollTo({ top: Math.max(0, targetScroll), behavior })
  return true
}

// Toggle section expanded state
export function toggleSection(
  sectionType: ResumeSectionType,
  expandedSections: Set<ResumeSectionType>,
  setExpandedSections: React.Dispatch<React.SetStateAction<Set<ResumeSectionType>>>
) {
  setExpandedSections((prev) => {
    const newSet = new Set(prev)
    if (newSet.has(sectionType)) {
      newSet.delete(sectionType)
    } else {
      newSet.add(sectionType)
    }
    return newSet
  })
}

// Scroll to last item in section
export function scrollToLastItem(
  selector: string,
  leftPanelRef: React.RefObject<HTMLDivElement>,
  delay: number = 200
) {
  setTimeout(() => {
    const items = document.querySelectorAll(selector)
    if (items.length > 0 && leftPanelRef.current) {
      const lastItem = items[items.length - 1] as HTMLElement
      scrollElementIntoPanelIfNeeded(lastItem, leftPanelRef.current)
    }
  }, delay)
}

export function scrollToSelectorInLeftPanel(
  leftPanelRef: React.RefObject<HTMLDivElement>,
  selector: string,
  options?: { initialDelayMs?: number }
) {
  const delay = options?.initialDelayMs ?? 100
  setTimeout(() => {
    const element = document.querySelector(selector)
    if (element && leftPanelRef.current) {
      scrollElementIntoPanelIfNeeded(element as HTMLElement, leftPanelRef.current)
    }
  }, delay)
}

// Scroll to element in left panel
export function scrollToElementInLeftPanel(
  element: Element | null,
  leftPanelRef: React.RefObject<HTMLDivElement>,
  delay: number = 100
) {
  if (element && leftPanelRef.current) {
    setTimeout(() => {
      scrollElementIntoPanelIfNeeded(element as HTMLElement, leftPanelRef.current!)
    }, delay)
  }
}
